import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

import { useAppContext } from '../context/AppContext';
import { formatCurrency, isoNamingForSexToAnredeMap } from '../util/helper';
import { validate } from '../util/preConfirmationValidator';
import { applicationService } from '../backend/applicationService';
import countryCodes from '../static/countryCodes.json';

import { selectCalculatedPremium } from '../context/util/selectors';
import { useGlobalError } from '../context/GlobalErrorContext';
import GlobalErrorAlert from '../components/GlobalErrorAlert';

import './Zusammenfassung.css';

function Zusammenfassung() {
  const navigate = useNavigate();
  //const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state, dispatch } = useAppContext();
  const { showGlobalError, clearGlobalError } = useGlobalError();

  const [summary, setSummary] = useState(null);
  const [vertragsunterlagenUrl, setVertragsunterlagenUrl] = useState('');
  const [datenverarbeitungUrl, setDatenverarbeitungUrl] = useState('');

  useEffect(() => {
    // Initialize page
    initializePage();
  }, []);

  useEffect(() => {
    // Zusammenfassung aus den gesammelten Daten erstellen
    console.log('state.applicationObject', state);

    const applicant = state.qualifiedApplication.applicant;

    const applicationSummary = {
      personalData: {
        anrede: isoNamingForSexToAnredeMap[applicant.sex] ?? '',
        vorname: applicant.firstname,
        nachname: applicant.surname,
        email: applicant.contact.find(c => c.type === 'email')?.data ?? '',
        telefon: applicant.contact.find(c => c.type === 'phone')?.data ?? '',
        geburtsdatum: applicant.birthdate,
        staatsangehoerigkeit: countryCodes.find(country => country.code === applicant.nationalityCode)?.label ?? '',
        strasse: applicant.address.street,
        hausnummer: applicant.address.streetNo,
        plz: applicant.address.zip,
        ort: applicant.address.city,
      },
      insuranceData: {
        versicherungssumme: state.formData.versicherungssumme,
        versicherungsbeginn: state.qualifiedApplication.modalities.beginDate,
        zahlungsintervall: state.qualifiedApplication.modalities.period.code,
        selectedOptions: state.qualifiedApplication.chosenProductsAndOptions[0].options || [],
        calculatedPremium: selectCalculatedPremium(state),
      },
      paymentData: {
        kontoinhaber: state.formData.kontoinhaber,
        iban: state.formData.iban,
      },
      applicationData: {
        sessionId: state.sessionId,
        applicationId: state.applicationId,
        timestamp: new Date().toISOString(),
      },
    };

    setSummary(applicationSummary);
  }, [state]);

  const initializePage = async () => {
    setVertragsunterlagenUrl('https://static.assona.com/RuV-Hardware/RUV_HW_2025/2025-06-28_DE_DE_RUV_HWV.pdf');
    setDatenverarbeitungUrl('https://static.assona.com/RuV-Hardware/RUV_HW_2025/AS_Datenschutz_assona_R+V_2025.pdf');
  };

  const formatDate = dateString => {
    if (!dateString) return '-';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      return format(date, 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateString;
    }
  };

  const getPaymentIntervalLabel = code => {
    const intervals = {
      MONTHLY_CONTINUOUS: 'monatlich',
      QUARTERLY_CONTINUOUS: 'vierteljährlich',
      SIX_MONTH_CONTINUOUS: 'halbjährlich',
      ANNUALLY_CONTINUOUS: 'jährlich',
    };
    return intervals[code] || code;
  };

  const handleSubmit = async () => {
    clearGlobalError();

    const validation = validate(state.qualifiedApplication);
    if (!validation.isValid) {
      console.error('Antrag kann nicht abgeschickt werden: ', validation.errors);
      showGlobalError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      return;
    }

    // Finalen Antrag erstellen und senden
    const response = await applicationService.signQualifiedApplication(
      state.applicationId,
      state.qualifiedApplication,
      state.sessionId,
    );

    if (response.success) {
      dispatch({
        type: 'SET_QUALIFIED_APPLICATION',
        payload: {
          qualifiedApplication: response.data,
        },
      });

      dispatch({ type: 'SET_CURRENT_STEP', payload: 6 });
      state.applicationObject = response.data;
      navigate('/danke');
    } else {
      if (response.status === 409) {
        showGlobalError('Dein Antrag wurde bereits erfasst. Du solltest in Kürze eine E-Mail erhalten.');
      } else {
        showGlobalError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    }
  };

  const handleDownload = async (documentUrl, documentFilename) => {
    if (documentUrl) {
      try {
        // Simuliere Download
        const link = document.createElement('a');
        link.href = documentUrl;
        link.download = documentFilename || 'vertragsunterlagen.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Fehler beim Download:', error);
      }
    }
  };

  const handleEdit = section => {
    dispatch({ type: 'SET_EDIT_MODE', payload: true });
    switch (section) {
      case 'personal':
        dispatch({ type: 'SET_CURRENT_STEP', payload: 2 });
        navigate('/persoenliche-angaben');
        break;
      case 'insurance':
        dispatch({ type: 'SET_CURRENT_STEP', payload: 1 });
        navigate('/tarif-berechnen');
        break;
      case 'payment':
        dispatch({ type: 'SET_CURRENT_STEP', payload: 4 });
        navigate('/deine-zahlungsdaten');
        break;
      default:
        break;
    }
  };

  if (!summary) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box className="wrapper" role="main" aria-label="Zusammenfassung und beitragspflichtig beantragen">
        <Container className="main-content-scroll-container">
          <Box className="main-content-with-sidebar">
            {/* --- PAGE CONTENT  --- */}
            <Box className="page-content">
              {/* NEU */}

              <Typography variant="h1" component="h1">
                Zusammenfassung
              </Typography>
              <GlobalErrorAlert sx={{ mt: 1 }} />

              <Box className="insured-summary-box" sx={{ mt: 3 }}>
                {/* Versicherungsdaten */}
                <Box className="insured-summary-box-part-with-edit" id="jumpmark-maincontent" tabIndex={-1}>
                  <List className="insured-summary-list-with-edit">
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Deine Versicherungssumme
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="p">
                          {formatCurrency(summary.insuranceData.versicherungssumme || 0)}
                        </Typography>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Deine Zusatzbausteine
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="div">
                          {!summary.insuranceData.selectedOptions ||
                            (summary.insuranceData.selectedOptions.length === 0 && 'Keine Zusatzbausteine gewählt') || (
                              <Box>
                                {summary.insuranceData.selectedOptions.map((opt, index) => (
                                  <Box key={index} className="options-list-entry">
                                    <Box component="span" className="summary-value options-icon-box">
                                      <CheckIcon className="options-icon" />
                                    </Box>{' '}
                                    {/* Symbol vor jeder Option */}
                                    <Box component="span" className="summary-value">
                                      {opt.label}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                        </Typography>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Dein Beitrag
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="p">
                          {formatCurrency(summary.insuranceData.calculatedPremium)} /{' '}
                          {getPaymentIntervalLabel(summary.insuranceData.zahlungsintervall)}
                        </Typography>
                        <Typography className="summary-value-info" variant="body2" component="p">
                          (inkl. 19% VersSt.)
                        </Typography>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Deine Zahlungsweise
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="p">
                          {getPaymentIntervalLabel(summary.insuranceData.zahlungsintervall)}
                        </Typography>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Dein Versicherungsbeginn
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="p">
                          {formatDate(summary.insuranceData.versicherungsbeginn)}
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>
                  <Button
                    className="button-edit-data"
                    aria-label="Tarifdaten bearbeiten" // Beschreibender Text für Screenreader
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => handleEdit('insurance')}
                    sx={{
                      '&:focus-visible': {
                        outline: '2px solid var(--color-primary-text)',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                      },
                    }}
                    data-testid="edit-tarif-button"
                  ></Button>
                </Box>

                <Divider className="divider-summary" orientation="vertical" aria-hidden="true" />

                {/* Zahldaten */}

                <Box className="insured-summary-box-part-with-edit">
                  <List className="insured-summary-list-with-edit">
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Kontoinhaber*in
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="p">
                          {summary.paymentData.kontoinhaber}
                        </Typography>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          IBAN
                        </Typography>
                        <Typography className="summary-value" variant="body2" component="p">
                          {summary.paymentData.iban}
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>
                  <Button
                    className="button-edit-data"
                    aria-label="Zahlungsdaten bearbeiten" // Beschreibender Text für Screenreader
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => handleEdit('payment')}
                    sx={{
                      '&:focus-visible': {
                        outline: '2px solid var(--color-primary-text)',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                      },
                    }}
                    data-testid="edit-zahlungsdaten-button"
                  ></Button>
                </Box>

                <Divider className="divider-summary" orientation="vertical" aria-hidden="true" />

                {/* Persönliche Daten */}

                <Box className="insured-summary-box-part-with-edit">
                  <List className="insured-summary-list-with-edit">
                    <ListItem disablePadding>
                      <Box>
                        <Typography className="summary-label" variant="h2" component="h2">
                          Persönliche Daten
                        </Typography>
                        <Box>
                          <Typography className="summary-value personal-data" variant="body2" component="p">
                            {`${summary.personalData.vorname} ${summary.personalData.nachname}`}
                          </Typography>
                          <Typography className="summary-value personal-data" variant="body2" component="p">
                            {`${summary.personalData.strasse} ${summary.personalData.hausnummer}`}
                          </Typography>
                          <Typography className="summary-value personal-data" variant="body2" component="p">
                            {`${summary.personalData.plz} ${summary.personalData.ort}`}
                          </Typography>
                          <Typography className="summary-value personal-data" variant="body2" component="p">
                            E-Mail-Adresse: {summary.personalData.email}
                          </Typography>
                          {summary.personalData.telefon && (
                            <Typography className="summary-value personal-data" variant="body2" component="p">
                              Telefonnummer: {summary.personalData.telefon}
                            </Typography>
                          )}
                          <Typography className="summary-value personal-data" variant="body2" component="p">
                            Staatsangehörigkeit: {summary.personalData.staatsangehoerigkeit}
                          </Typography>
                          <Typography className="summary-value personal-data" variant="body2" component="p">
                            Geburtsdatum: {formatDate(summary.personalData.geburtsdatum)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  </List>
                  <Button
                    className="button-edit-data"
                    aria-label="Persönliche Angaben bearbeiten" // Beschreibender Text für Screenreader
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => handleEdit('personal')}
                    sx={{
                      '&:focus-visible': {
                        outline: '2px solid var(--color-primary-text)',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                      },
                    }}
                    data-testid="edit-persoenliche-angaben-button"
                  ></Button>
                </Box>

                <Divider className="divider-summary" orientation="vertical" aria-hidden="true" />

                {/* Downloadbereich */}

                <Box className="insured-summary-box-part-with-edit">
                  <List className="insured-summary-list-with-edit">
                    <ListItem disablePadding>
                      <Box className="download-box">
                        <Box className="download-box-button">
                          <IconButton
                            className="download-button"
                            aria-label="Vertragsunterlagen herunterladen"
                            onClick={() => handleDownload(vertragsunterlagenUrl, 'vertragsunterlagen.pdf')}
                            sx={{
                              '&:focus-visible': {
                                outline: '2px solid var(--color-primary-text)',
                                outlineOffset: '2px',
                                borderRadius: '2px',
                              },
                            }}
                          >
                            <FileDownloadOutlinedIcon sx={{ fontSize: 30 }} />
                          </IconButton>
                        </Box>
                        <Box className="download-box-text">
                          <Typography className="summary-label" variant="h2" component="h2">
                            Vertragsunterlagen
                          </Typography>
                          <Typography variant="body2" component="p">
                            Versicherungsbedingungen und Kundeninformation, Produktinformationsblatt,
                            Vertragsinformationen inkl. Widerrufsbelehrung
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                    <ListItem disablePadding>
                      <Box className="download-box">
                        <Box className="download-box-button">
                          <IconButton
                            className="download-button"
                            aria-label="Merkblatt zur Datenverarbeitung herunterladen"
                            onClick={() => handleDownload(datenverarbeitungUrl, 'datenverarbeitung-merkblatt.pdf')}
                            sx={{
                              '&:focus-visible': {
                                outline: '2px solid var(--color-primary-text)',
                                outlineOffset: '2px',
                                borderRadius: '2px',
                              },
                            }}
                          >
                            <FileDownloadOutlinedIcon sx={{ fontSize: 30 }} />
                          </IconButton>
                        </Box>
                        <Box className="download-box-text">
                          <Typography className="summary-label" variant="h2" component="h2">
                            Merkblatt zur Datenverarbeitung
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  </List>
                </Box>
              </Box>

              {/* Navigation Buttons */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Box className="button-container" sx={{ mt: 4 }}>
                  <Button
                    className="button-general button-action"
                    variant="contained"
                    type="submit"
                    onClick={handleSubmit}
                    aria-label="Beitragspflichtig beantragen"
                  >
                    Beitragspflichtig beantragen
                  </Button>
                </Box>
              </Box>
            </Box>{' '}
            {/* className="page-content" */}
            {/* --- ENDE PAGE CONTENT  --- */}
          </Box>{' '}
          {/* className="main-content-with-sidebar" */}
        </Container>{' '}
        {/* className="main-content-scroll-container" */}
      </Box>{' '}
      {/* className="wrapper" */}
    </LocalizationProvider>
  );
}

export default Zusammenfassung;
